// ============================================================================
// STORE.QUEUEPINGS.JS — Queue Pings (sector-targeted operational attention)
//
// Queue Ping = one operational fire → one responsible sector role → many
// receivers, acknowledge-only. Distinct from Referral Pings (which are
// referral-bound, 1:1, with replies). Tenant-level, NON-PHI, not referral-bound.
//
//   Parent:    tenants/{tenantId}/queuePings/{queuePingId}
//   Delivery:  tenants/{tenantId}/queuePings/{queuePingId}/deliveries/{uid}
//
// Creation + dismissal are server-side (Cloud Functions) so the recipient list
// is trustworthy and cannot be forged. Acknowledgment is a client self-write to
// the recipient's own delivery doc.
//
// NO PHI: no patient identifiers, no referral IDs, no document names. Optional
// note is short, length-capped, and PHI-scanned.
// ============================================================================

import { getApp } from 'firebase/app';
import {
  getFirestore, doc, updateDoc, serverTimestamp,
  collection, collectionGroup, query, where, orderBy, onSnapshot, getDocs, limit,
} from 'firebase/firestore';
import { PATHS } from './firestorePaths.js';

function getDb() {
  return getFirestore(getApp());
}

// ── Enums ─────────────────────────────────────────────────────────────────────

export const QUEUE_PING_STATUS = {
  OPEN:      'OPEN',
  DISMISSED: 'DISMISSED',
  CLEARED:   'CLEARED', // reserved; not used in v1 (no auto-clear yet)
};

export const QUEUE_PING_DELIVERY_STATUS = {
  UNREAD:       'UNREAD',
  ACKNOWLEDGED: 'ACKNOWLEDGED',
};

export const QUEUE_PING_SECTOR = {
  INTAKE:      'INTAKE',
  TRIAGE:      'TRIAGE',
  SCHEDULING:  'SCHEDULING',
  CLOSED_LOOP: 'CLOSED_LOOP',
};

// Sector → the single responsible receiving role.
export const QUEUE_PING_SECTOR_ROLE_MAP = {
  INTAKE:      'intake_coordinator',
  TRIAGE:      'triage_nurse',
  SCHEDULING:  'scheduler',
  CLOSED_LOOP: 'specialist_admin',
};

// Sector → workspace route key (+ optional navState) for the recipient "Open Queue".
export const OPEN_QUEUE_ROUTE_MAP = {
  INTAKE:      { route: 'workqueue' },
  TRIAGE:      { route: 'triage-queue', navState: { tab: 'PENDING' } },
  SCHEDULING:  { route: 'scheduling' },
  CLOSED_LOOP: { route: 'specialist-admin-workplace', navState: { tab: 'needs_close_loop' } },
};

// Canonical purposes (one per fire type). actionNeeded is canned (never free text).
export const QUEUE_PING_PURPOSE = {
  INTAKE_UNCLAIMED_NEW:            'INTAKE_UNCLAIMED_NEW',
  INTAKE_NEEDS_INFO:               'INTAKE_NEEDS_INFO',
  TRIAGE_STUCK:                    'TRIAGE_STUCK',
  SCHEDULING_BACKLOG:              'SCHEDULING_BACKLOG',
  LONG_DATED_APPOINTMENTS:         'LONG_DATED_APPOINTMENTS',
  CLOSED_LOOP_PENDING:             'CLOSED_LOOP_PENDING',
  CLOSED_LOOP_UNRECONCILED_VISITS: 'CLOSED_LOOP_UNRECONCILED_VISITS',
};

export const QUEUE_PING_PURPOSE_LABELS = {
  INTAKE_UNCLAIMED_NEW:            'Claim New Referrals',
  INTAKE_NEEDS_INFO:               'Clear Needs Info Backlog',
  TRIAGE_STUCK:                    'Review Triage Backlog',
  SCHEDULING_BACKLOG:              'Clear Scheduling Backlog',
  LONG_DATED_APPOINTMENTS:         'Review Long-Dated Appointments',
  CLOSED_LOOP_PENDING:             'Close Seen Referrals',
  CLOSED_LOOP_UNRECONCILED_VISITS: 'Reconcile Past Visits',
};

export const QUEUE_PING_ACTION_TEXT = {
  INTAKE_UNCLAIMED_NEW:            'New referrals are waiting for first ownership. Open the Workqueue and claim them.',
  INTAKE_NEEDS_INFO:               'Needs Info is backing up. Open Workqueue > Needs Info, review stuck items, and follow up on referrals that need action.',
  TRIAGE_STUCK:                    'Clinical review has stuck referrals. Open the Triage Queue and review items needing action.',
  SCHEDULING_BACKLOG:              'Ready-to-schedule referrals are backing up. Open Scheduling and move ready referrals forward.',
  LONG_DATED_APPOINTMENTS:         'Some appointments are scheduled too far out. Open Scheduling and review capacity or priority.',
  CLOSED_LOOP_PENDING:             'Seen referrals are waiting for closed-loop completion. Open Needs Action and close eligible referrals.',
  CLOSED_LOOP_UNRECONCILED_VISITS: 'Past visits still need Seen / No-Show reconciliation. Open Needs Action and reconcile them.',
};

// Purpose → sector (used to derive targetSector/targetRole at creation).
export const QUEUE_PING_PURPOSE_SECTOR = {
  INTAKE_UNCLAIMED_NEW:            QUEUE_PING_SECTOR.INTAKE,
  INTAKE_NEEDS_INFO:               QUEUE_PING_SECTOR.INTAKE,
  TRIAGE_STUCK:                    QUEUE_PING_SECTOR.TRIAGE,
  SCHEDULING_BACKLOG:              QUEUE_PING_SECTOR.SCHEDULING,
  LONG_DATED_APPOINTMENTS:         QUEUE_PING_SECTOR.SCHEDULING,
  CLOSED_LOOP_PENDING:             QUEUE_PING_SECTOR.CLOSED_LOOP,
  CLOSED_LOOP_UNRECONCILED_VISITS: QUEUE_PING_SECTOR.CLOSED_LOOP,
};

export const QUEUE_PING_NOTE_MAX_LENGTH = 280;

// ── Client writes ─────────────────────────────────────────────────────────────

/** Recipient acknowledges their own delivery (UNREAD → ACKNOWLEDGED). Self-write. */
export async function acknowledgeQueuePingDelivery(tenantId, queuePingId, uid) {
  if (!tenantId || !queuePingId || !uid) {
    throw new Error('acknowledgeQueuePingDelivery: tenantId, queuePingId, uid are required');
  }
  const db = getDb();
  await updateDoc(doc(db, PATHS.queuePingDelivery(tenantId, queuePingId, uid)), {
    status:          QUEUE_PING_DELIVERY_STATUS.ACKNOWLEDGED,
    acknowledgedAt:  serverTimestamp(),
    readAt:          serverTimestamp(),
  });
}

// ── Reads / subscriptions ───────────────────────────────────────────────────

function mapDelivery(d) {
  return { id: d.id, queuePingId: d.ref.parent.parent?.id ?? null, ...d.data() };
}

/** Sort helper: newest first by parentCreatedAt, then createdAt. Tolerates docs
 *  that are missing either timestamp (treated as 0 / oldest). */
function deliverySortMs(d) {
  const ts = d.parentCreatedAt ?? d.createdAt ?? null;
  if (!ts) return 0;
  if (ts.toMillis) return ts.toMillis();
  if (ts.seconds)  return ts.seconds * 1000;
  const parsed = Date.parse(ts);
  return Number.isNaN(parsed) ? 0 : parsed;
}

/**
 * Live: Queue Ping deliveries addressed to the current user (newest first).
 * Collection-group over deliveries, tenant + uid scoped. Carries denormalized
 * display fields so the inbox renders without parent fan-out reads.
 *
 * NOTE: no server-side orderBy. Firestore's orderBy silently EXCLUDES documents
 * that lack the ordered field, which would hide deliveries written without a
 * createdAt timestamp. We filter on equality only (served by the existing
 * tenantId+uid index prefix) and sort client-side, so a recipient always sees
 * every delivery addressed to them regardless of timestamp completeness.
 */
export function subscribeQueuePingDeliveriesForUser(tenantId, uid, { onData, onError } = {}, max = 100) {
  if (!tenantId || !uid) { onData?.([]); return () => {}; }
  const db = getDb();
  const q = query(
    collectionGroup(db, 'deliveries'),
    where('tenantId', '==', tenantId),
    where('uid', '==', uid),
    limit(max),
  );
  return onSnapshot(
    q,
    (snap) => {
      const rows = snap.docs.map(mapDelivery).sort((a, b) => deliverySortMs(b) - deliverySortMs(a));
      onData?.(rows);
    },
    (err) => {
      console.error('[QueuePings] delivery query error:', err?.code || err?.message);
      onError?.(err);
    },
  );
}

/** Live: Queue Pings the current user sent (sender view + ack summaries). */
export function subscribeQueuePingsForSender(tenantId, uid, { onData, onError } = {}, max = 100) {
  if (!tenantId || !uid) { onData?.([]); return () => {}; }
  const db = getDb();
  const q = query(
    collection(db, PATHS.col.queuePings(tenantId)),
    where('senderUid', '==', uid),
    orderBy('createdAt', 'desc'),
    limit(max),
  );
  return onSnapshot(q, (snap) => onData?.(snap.docs.map((d) => ({ id: d.id, ...d.data() }))), (err) => onError?.(err));
}

/** One-shot: deliveries for a Queue Ping (sender/admin ack summary). */
export async function listQueuePingDeliveries(tenantId, queuePingId, max = 500) {
  if (!tenantId || !queuePingId) return [];
  const db = getDb();
  const snap = await getDocs(query(
    collection(db, PATHS.col.queuePingDeliveries(tenantId, queuePingId)),
    limit(max),
  ));
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}
